import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { mixpanelTrack } from '../utils/mixpanel.util';
import { WebStorageService } from './web-storage.service';

/**
 * Query-param key the deposit flow reads to label where an attempt came from.
 *
 * `AssetDepositComponent` passes `?from=<value>` straight through to
 * `DepositAnalyticsService.start()` as `entry_point`, so whatever a CTA puts here is
 * the value every downstream funnel event is broken down by.
 */
export const DEPOSIT_ENTRY_POINT_PARAM = 'from';

/**
 * Every place a user can enter the deposit flow from.
 *
 * A closed union rather than a bare string: `strictTemplates` is on, so a typo in a
 * `[appDepositCta]` binding fails the build instead of quietly creating a second,
 * near-identical bucket in Mixpanel that nobody notices until a report looks wrong.
 *
 * The `-redirect` entries are NOT clicks — they are the automatic countdowns that send
 * a user with no assets to the deposit flow. They never fire `Deposit CTA Clicked`;
 * they only label `Deposit Started`, so those arrivals stop being counted as `direct`.
 */
export type DepositEntryPoint =
    | 'header'
    | 'dashboard-hero-slide'
    | 'dashboard-shortcut'
    | 'dashboard-checklist'
    | 'dashboard-kyc-card'
    | 'kyc-complete'
    | 'my-deposits-empty'
    | 'not-found'
    // Pre-dates this directive: `MyDepositsComponent.onEditAsset` sends it when resuming a
    // submitted deposit, and reports already use the value. Listed so the union documents
    // every `entry_point` in the data, not only the ones fired from here.
    | 'my-deposits'
    | 'borrow-empty-redirect'
    | 'borrow-history-empty-redirect'
    | 'your-items-empty-redirect';

/**
 * Router query params that tag an arrival with its entry point.
 *
 * Used by the `[routerLink]` CTAs so the link's `[queryParams]` and the click event
 * agree on one id, and by the `router.navigate` call sites for the same reason.
 */
export function depositEntryParams(entryPoint: DepositEntryPoint): Params {
    return { [DEPOSIT_ENTRY_POINT_PARAM]: entryPoint };
}

/** The deposit flow's route, as it appears in the data-driven dashboard shortcut lists. */
export const DEPOSIT_ROUTE = '/deposit-asset';

/** Mixpanel truncates string properties past 255 characters; button text is capped well below. */
const MAX_LABEL_LENGTH = 80;

/**
 * Tracking for the deposit call-to-action buttons scattered across the app.
 *
 * `DepositAnalyticsService` covers everything that happens *inside* the three-screen
 * flow, but it cannot see the step before it: which button someone pressed to get
 * there, and — the part no in-flow event can ever record — the users who press a
 * deposit button and never arrive at all, because they are bounced to sign-in first.
 *
 * `entry_point` is deliberately the same property name the in-flow events use, so one
 * Mixpanel funnel reads end to end:
 *
 *   Deposit CTA Clicked → Deposit Started → Deposit Submitted
 *
 * broken down by `entry_point` at every step. That is what answers "which placement
 * actually produces deposits" rather than merely "which placement gets clicked".
 */
@Injectable({ providedIn: 'root' })
export class DepositCtaAnalyticsService {
    constructor(private webStorageService: WebStorageService) {}

    /**
     * Fire `Deposit CTA Clicked`.
     *
     * @param entryPoint - which CTA was pressed; becomes `entry_point`
     * @param label - the button's visible text, when there is one worth reporting
     */
    ctaClicked(entryPoint: DepositEntryPoint, label?: string): void {
        const user = this.readStoredUser();
        const trimmedLabel = (label ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL_LENGTH);

        mixpanelTrack('Deposit CTA Clicked', {
            entry_point: entryPoint,
            ...(trimmedLabel ? { cta_label: trimmedLabel } : {}),
            // A signed-out click never reaches the flow — `AssetDepositComponent` redirects
            // it to sign-in — so without this flag those users would look like a deposit
            // that started and instantly vanished, rather than the sign-up prompt they are.
            is_signed_in: !!user?._id,
            ...(user?._id ? { user_id: user._id } : {}),
        });
    }

    /**
     * `ctaClicked` for a link whose destination is data-driven — the dashboard shortcut
     * carousels render one `<a>` for a list that happens to contain the deposit route
     * alongside three others. Silently does nothing for the other three.
     */
    shortcutClicked(routerLink: string, entryPoint: DepositEntryPoint, label?: string): void {
        if (routerLink !== DEPOSIT_ROUTE) return;
        this.ctaClicked(entryPoint, label);
    }

    /**
     * `depositEntryParams` for the same data-driven links: the entry point for the deposit
     * card, and `null` — which `routerLink` treats as "no query params" — for the rest, so
     * a Browse market or Lend funds link is not tagged with a deposit entry point.
     */
    shortcutParams(routerLink: string, entryPoint: DepositEntryPoint): Params | null {
        return routerLink === DEPOSIT_ROUTE ? depositEntryParams(entryPoint) : null;
    }

    /**
     * The signed-in user, read the same way `AssetDepositComponent` reads it.
     *
     * Goes through `WebStorageService` rather than `localStorage` directly: `user` is one
     * of its state-backed keys, so it is not necessarily in localStorage at all.
     */
    private readStoredUser(): { _id?: string } | null {
        try {
            const raw = this.webStorageService.getLocalStorage('user');
            return raw ? JSON.parse(raw) : null;
        } catch {
            // no-op: analytics must not break user flows.
            return null;
        }
    }
}
